import api from './api';

const C = (token) => `/api/ai-interview/c/${token}`;
const MP = { headers: { 'Content-Type': 'multipart/form-data' } };

export const errMsg = (e, fallback = 'Something went wrong') => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : fallback;
};

export const aiInterviewsAPI = {
  settings: () => api.get('/api/ai-interviews/settings'),
  saveSettings: (data) => api.put('/api/ai-interviews/settings', data),
  stats: () => api.get('/api/ai-interviews/stats'),
  queue: (status = 'pending') => api.get(`/api/ai-interviews/integrity/queue?status=${status}`),
  accommodations: (status = 'pending') => api.get(`/api/ai-interviews/accommodations?status=${status}`),
  decideAccommodation: (id, data) => api.post(`/api/ai-interviews/accommodations/${id}/decide`, data),
  fairness: () => api.get('/api/ai-interviews/fairness'),
  biasCsv: () => api.get('/api/ai-interviews/bias-audit.csv', { responseType: 'blob' }),
  events: () => api.get('/api/ai-interviews/events'),
  usage: () => api.get('/api/ai-interviews/usage'),
  dataSummary: () => api.get('/api/ai-interviews/data-processing-summary'),
  plans: (jobId) => api.get(`/api/ai-interview-plans${jobId ? `?job_id=${jobId}` : ''}`),
  plan: (id) => api.get(`/api/ai-interview-plans/${id}`),
  createPlan: (jobId, data) => api.post(`/api/jobs/${jobId}/ai-interview-plans`, data),
  updatePlan: (id, data) => api.put(`/api/ai-interview-plans/${id}`, data),
  regeneratePlan: (id) => api.post(`/api/ai-interview-plans/${id}/regenerate`),
  inviteCandidates: (jobId, planId) => api.get(`/api/jobs/${jobId}/ai-interview-candidates?plan_id=${planId}`),
  invite: (planId, data) => api.post(`/api/ai-interview-plans/${planId}/invite`, data),
  list: (params = {}) => api.get('/api/ai-interviews', { params }),
  get: (id) => api.get(`/api/ai-interviews/${id}`),
  transcript: (id) => api.get(`/api/ai-interviews/${id}/transcript`),
  signals: (id) => api.get(`/api/ai-interviews/${id}/signals`),
  saveBlueprint: (id, blueprint) => api.put(`/api/ai-interviews/${id}/blueprint`, { blueprint }),
  regenerateBlueprint: (id) => api.post(`/api/ai-interviews/${id}/blueprint/regenerate`),
  action: (id, action) => api.post(`/api/ai-interviews/${id}/action`, { action }),
  override: (id, data) => api.post(`/api/ai-interviews/${id}/override`, data),
  annotate: (id, data) => api.post(`/api/ai-interviews/${id}/annotate`, data),
  decision: (id, data) => api.post(`/api/ai-interviews/${id}/decision`, data),
  review: (id, data) => api.post(`/api/ai-interviews/${id}/integrity/review`, data),
  recordingParts: (id) => api.get(`/api/ai-interviews/${id}/recording`),
  recording: (id, part) => api.get(`/api/ai-interviews/${id}/recording/${part}`, { responseType: 'blob' }),
  media: (id, mediaId) => api.get(`/api/ai-interviews/${id}/media/${mediaId}`, { responseType: 'blob' }),
  deleteData: (id, data) => api.post(`/api/ai-interviews/${id}/delete-data`, data),
  trigger: (data) => api.post('/api/ai-interviews/trigger', data),
};

export const aiCandidateAPI = {
  context: (t) => api.get(C(t)),
  consent: (t, data) => api.post(`${C(t)}/consent`, data),
  decline: (t, data) => api.post(`${C(t)}/decline`, data),
  accommodation: (t, data) => api.post(`${C(t)}/accommodation`, data),
  techCheck: (t, data) => api.post(`${C(t)}/tech-check`, data),
  identity: (t, form) => api.post(`${C(t)}/identity`, form, MP),
  start: (t) => api.post(`${C(t)}/start`),
  turn: (t, form) => api.post(`${C(t)}/turn`, form, { ...MP, timeout: 90000 }),
  speechUrl: (t, seq) => `${process.env.REACT_APP_BACKEND_URL}${C(t)}/speech/${seq}`,
  phraseUrl: (t, key) => `${process.env.REACT_APP_BACKEND_URL}${C(t)}/phrase/${key}`,
  chunk: (t, form) => api.post(`${C(t)}/media-chunk`, form, { ...MP, timeout: 60000 }),
  snapshot: (t, form) => api.post(`${C(t)}/snapshot`, form, MP),
  signals: (t, signals) => api.post(`${C(t)}/signals`, { signals }),
  heartbeat: (t, quality) => api.post(`${C(t)}/heartbeat`, { quality }),
  reverify: (t, data) => api.post(`${C(t)}/reverify`, data),
  end: (t, reason) => api.post(`${C(t)}/end`, { reason }),
  explanation: (t, text) => api.post(`${C(t)}/explanation`, { text }),
  deleteData: (t) => api.post(`${C(t)}/delete-data`, { confirm: 'DELETE' }),
};

export const STATUS_META = {
  invited: { label: 'Invited', tone: 'slate' },
  opened: { label: 'Opened', tone: 'sky' },
  consented: { label: 'Consented', tone: 'sky' },
  starting: { label: 'Starting', tone: 'teal' },
  in_progress: { label: 'In progress', tone: 'teal' },
  interrupted: { label: 'Interrupted', tone: 'amber' },
  completed: { label: 'Completed', tone: 'teal' },
  scoring: { label: 'Scoring', tone: 'teal' },
  report_ready: { label: 'Report ready', tone: 'green' },
  score_error: { label: 'Scoring failed', tone: 'red' },
  declined: { label: 'Declined', tone: 'red' },
  expired: { label: 'Expired', tone: 'slate' },
  revoked: { label: 'Revoked', tone: 'slate' },
  data_deleted: { label: 'Data deleted', tone: 'slate' },
};

export const TIER_META = {
  clear: { label: 'Clear', tone: 'green' },
  review_suggested: { label: 'Review suggested', tone: 'amber' },
  mandatory_review: { label: 'Mandatory review', tone: 'red' },
};

export const SEGMENT_LABELS = { setup: 'Setup', background: 'Background', deep_dive: 'Deep dive', situational: 'Situational', close: 'Close' };

export const fmtClock = (ms) => {
  const s = Math.max(0, Math.round((ms || 0) / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};
